import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, keyframes } from '@mui/material/styles';
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

export interface SegmentProps {
  ok: number;
  error: number;
  total: number;
}

// Hardcoded em vez do theme: error.main/info.main têm contraste ruim no fundo escuro deste componente.
const SEG_ERROR_COLOR = '#ff5252';
const SEG_ALREADY_SYNCED_COLOR = '#4dabf7';

interface ProgressDialogProps {
  open: boolean;
  onClose: () => void;
  onCancel?: () => void;
  onMinimize?: () => void;
  progress: number;
  status: 'loading' | 'success' | 'partial' | 'error' | 'canceled';
  statusMessage: string;
  title?: string;
  etaSeconds?: number | null;
  children?: ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md';
  segments?: SegmentProps;
  segmentDevices?: Array<{ name: string; status: 'ok' | 'error' | 'processing' | 'pending' | 'already_synced' }>;
  onRetry?: () => void;
  icon?: ReactNode;
  accentColor?: string;
}

const btnSx = {
  color: 'text.secondary',
  '&:hover': { backgroundColor: (t: { palette: { action: { hover: string } } }) => alpha(t.palette.action.hover, 0.2), color: 'text.primary' },
} as const;

const ProgressDialog: FC<ProgressDialogProps> = ({
  open,
  onClose,
  onCancel,
  onMinimize,
  progress,
  status,
  statusMessage,
  title = 'Processando',
  etaSeconds,
  children,
  maxWidth = 'xs',
  segments,
  segmentDevices,
  onRetry,
  icon,
  accentColor,
}) => {
  const formatEta = (seconds: number) => {
    const safe = Math.max(1, Math.floor(seconds));
    const h = Math.floor(safe / 3600);
    const m = Math.floor((safe % 3600) / 60);
    const s = safe % 60;
    if (h > 0) return `~${h}h ${m > 0 ? `${m}min` : ''}`.trim();
    if (safe >= 60) return `~${m} min`;
    return `~${s}s`;
  };

  const clampedProgress = Math.max(0, Math.min(100, progress));
  const isLoading = status === 'loading';
  const isDone    = !isLoading;

  const processingIdx = segments && isLoading && (segments.ok + segments.error) < segments.total
    ? segments.ok + segments.error
    : -1;

  // Contagem regressiva do ETA
  const [localEta, setLocalEta] = useState<number | null>(null);
  const etaIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (etaIntervalRef.current) clearInterval(etaIntervalRef.current);
    if (!isLoading || typeof etaSeconds !== 'number' || etaSeconds <= 0) { setLocalEta(null); return undefined; }
    setLocalEta(etaSeconds);
    etaIntervalRef.current = setInterval(() => {
      setLocalEta((prev) => (prev !== null && prev > 1 ? prev - 1 : null));
    }, 1000);
    return () => { if (etaIntervalRef.current) clearInterval(etaIntervalRef.current); };
  }, [isLoading, etaSeconds]);

  // Efeito fade-swap na troca de mensagem
  const [displayedMsg, setDisplayedMsg] = useState(statusMessage);
  const [msgVisible,   setMsgVisible]   = useState(true);
  const msgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (statusMessage === displayedMsg) return undefined;
    if (msgTimerRef.current) clearTimeout(msgTimerRef.current);
    setMsgVisible(false);
    msgTimerRef.current = setTimeout(() => { setDisplayedMsg(statusMessage); setMsgVisible(true); }, 130);
    return () => { if (msgTimerRef.current) clearTimeout(msgTimerRef.current); };
  }, [statusMessage, displayedMsg]);

  const resultMeta = {
    success: { icon: <CheckCircleOutlineIcon />, color: 'success.main',  label: statusMessage || 'Concluído com sucesso!' },
    partial: { icon: <WarningAmberRoundedIcon />, color: 'warning.main', label: statusMessage || 'Concluído com falhas'   },
    error:   { icon: <ErrorOutlineIcon />,        color: '#ff5252',      label: statusMessage || 'Ocorreu um erro'         },
    canceled:{ icon: <BlockRoundedIcon />,        color: 'warning.main', label: statusMessage || 'Operação cancelada'      },
  } as const;

  const closeBtnColor = { success: 'primary', partial: 'warning', error: 'error', canceled: 'warning' } as const;

  const progressLabel = isLoading ? 'Progresso'
    : status === 'success' ? 'Concluído'
    : status === 'partial' ? 'Parcial'
    : status === 'error'   ? 'Falhou'
    : 'Progresso';
  const progressColor = isLoading ? undefined
    : status === 'success' ? 'success.main'
    : status === 'partial' ? 'warning.main'
    : status === 'error'   ? '#ff5252'
    : undefined;

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={maxWidth}
      onClose={isDone ? onClose : undefined}
    >
      {/* ── Title ── */}
      <DialogTitle
        component="div"
        sx={{
          pb: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderTop: '3px solid',
          borderTopColor: isLoading
            ? accentColor ?? 'primary.main'
            : status === 'success'
            ? 'success.main'
            : status === 'error'
            ? '#ff5252'
            : 'warning.main',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isLoading && icon && (
              <Box sx={{ display: 'flex', fontSize: 20, color: accentColor ?? 'primary.main' }}>{icon}</Box>
            )}
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {title}
            </Typography>
          </Box>

          {isLoading && onMinimize ? (
            <Tooltip title="Minimizar">
              <IconButton aria-label="minimizar" onClick={onMinimize} size="small" sx={btnSx}>
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : isDone ? (
            <IconButton aria-label="fechar" onClick={onClose} size="small" sx={btnSx}>
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      </DialogTitle>

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2, pb: 1.5 }}>

        {/* % de progresso + barra — continua visível durante o carregamento/conclusão para manter estável a altura da modal */}
        {status !== 'canceled' && (isLoading || segmentDevices || segments) && (
          <Box sx={{ mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="body2" color={progressColor ?? 'text.secondary'} fontWeight={500}>{progressLabel}</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: progressColor ?? 'text.primary' }}>{`${Math.round(clampedProgress)}%`}</Typography>
            </Box>

            {segmentDevices ? (
              <Box sx={{ display: 'flex', gap: '3px', height: 8 }}>
                {segmentDevices.map((dev, i) => {
                  const isAlreadySynced = dev.status === 'already_synced';
                  const isOk   = dev.status === 'ok';
                  const isErr  = dev.status === 'error';
                  const isProc = dev.status === 'processing';
                  const label  = isAlreadySynced ? 'Já sincronizado'
                    : isOk ? 'Concluído' : isErr ? 'Falhou' : isProc ? 'Em andamento' : 'Aguardando';
                  return (
                    <Tooltip
                      // eslint-disable-next-line react/no-array-index-key
                      key={`seg-${i}`}
                      title={`${dev.name} — ${label}`}
                      placement="top"
                      arrow
                    >
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: '3px',
                          cursor: 'default',
                          backgroundColor: isOk  ? 'success.main'
                            : isAlreadySynced   ? SEG_ALREADY_SYNCED_COLOR
                            : isErr             ? SEG_ERROR_COLOR
                            : isProc            ? 'info.main'
                            : (t) => alpha(t.palette.info.main, 0.15),
                          animation: isProc ? `${pulse} 1.2s ease-in-out infinite` : 'none',
                          transition: 'background-color 0.4s ease',
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ) : segments ? (
              <Box sx={{ display: 'flex', gap: '3px', height: 8 }}>
                {Array.from({ length: segments.total }, (_, i) => {
                  const isOk   = i < segments.ok;
                  const isErr  = !isOk && i < segments.ok + segments.error;
                  const isProc = i === processingIdx;
                  return (
                    <Box
                      // eslint-disable-next-line react/no-array-index-key
                      key={`seg-${i}`}
                      sx={{
                        flex: 1,
                        borderRadius: '3px',
                        backgroundColor: isOk   ? 'success.main'
                          : isErr               ? SEG_ERROR_COLOR
                          : isProc              ? 'info.main'
                          : (t) => alpha(t.palette.info.main, 0.15),
                        animation: isProc ? `${pulse} 1.2s ease-in-out infinite` : 'none',
                        transition: 'background-color 0.4s ease',
                      }}
                    />
                  );
                })}
              </Box>
            ) : (
              <LinearProgress
                variant="determinate"
                value={clampedProgress}
                color="info"
                sx={{
                  width: '100%', borderRadius: 1, height: 8,
                  backgroundColor: (t) => alpha(t.palette.info.main, 0.15),
                  '& .MuiLinearProgress-bar': {
                    width: `${clampedProgress}% !important`,
                    transform: 'translateX(0) !important',
                    transition: 'width 280ms ease !important',
                  },
                }}
              />
            )}
          </Box>
        )}

        {/* Mensagem de carregamento — desaparece com fade enquanto a barra de progresso continua fixada acima */}
        <Fade in={isLoading} unmountOnExit timeout={{ enter: 250, exit: 200 }}>
          <Box sx={{ px: 1.5, py: 1, borderRadius: 1.5, backgroundColor: (t) => alpha(t.palette.info.main, 0.1), border: (t) => `1px solid ${alpha(t.palette.info.main, 0.3)}`, mb: 1 }}>
            <Fade in={msgVisible} timeout={{ enter: 200, exit: 130 }}>
              <Typography variant="body2" color="text.secondary">{displayedMsg}</Typography>
            </Fade>
            {localEta !== null && localEta > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Resta {formatEta(localEta)}
              </Typography>
            )}
          </Box>
        </Fade>

        {/* Banner de resultado concluído — surge com fade após a mensagem de carregamento desaparecer */}
        <Fade in={isDone} unmountOnExit timeout={{ enter: 300, exit: 150 }} style={{ transitionDelay: isDone ? '120ms' : '0ms' }}>
          <Box>
            {(() => {
              const meta = resultMeta[status as keyof typeof resultMeta];
              if (!meta) return null;
              return children ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: 1.25,
                    py: 0.9,
                    mb: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: (t) => alpha(typeof meta.color === 'string' && meta.color.startsWith('#')
                      ? meta.color
                      : t.palette[meta.color.split('.')[0] as 'success' | 'warning']?.main ?? meta.color, 0.3),
                    backgroundColor: (t) => alpha(typeof meta.color === 'string' && meta.color.startsWith('#')
                      ? meta.color
                      : t.palette[meta.color.split('.')[0] as 'success' | 'warning']?.main ?? meta.color, 0.08),
                    color: meta.color,
                  }}
                >
                  <Box sx={{ display: 'flex', fontSize: 20 }}>{meta.icon}</Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'inherit' }}>{meta.label}</Typography>
                </Box>
              ) : (
                <Box sx={{ color: meta.color, display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                  <Box sx={{ fontSize: 52, display: 'flex', mb: 1 }}>{meta.icon}</Box>
                  <Typography variant="body1" fontWeight={500}>{meta.label}</Typography>
                </Box>
              );
            })()}
          </Box>
        </Fade>

        {children && <Box>{children}</Box>}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions sx={{ px: 3, pb: 2, pt: 1.5, gap: 1, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
        {isLoading ? (
          <>
            <Button disabled color="secondary" variant="contained" sx={{ minWidth: 110 }}>Aguardando</Button>
            <Button onClick={onCancel} disabled={!onCancel} variant="contained" color="error" sx={{ minWidth: 110 }}>Cancelar</Button>
          </>
        ) : status === 'partial' && onRetry ? (
          <>
            <Button onClick={onRetry} variant="outlined" color="warning" sx={{ minWidth: 130 }}>
              Retentar falhas
            </Button>
            <Button onClick={onClose} variant="contained" color="warning" sx={{ minWidth: 110 }}>
              Fechar
            </Button>
          </>
        ) : (
          <Button
            onClick={onClose}
            variant="contained"
            color={isDone && status !== 'canceled' ? closeBtnColor[status as keyof typeof closeBtnColor] ?? 'primary' : 'warning'}
            sx={{ minWidth: 110 }}
          >
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProgressDialog;
